import { useMemo } from "react";

import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import type { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { useCopilotStore } from "src/unifiedCopilot/context/CopilotContext";
import { useGlobalContextualEntitiesContext } from "src/unifiedCopilot/context/globalContextualEntities";
import {
  ContextualEntity,
  contextualEntityKey,
  ReferenceContextualEntity,
} from "src/unifiedCopilot/context/types";

const isReferenceContextualEntity = (
  entity: ContextualEntity,
): entity is ReferenceContextualEntity => entity.kind === "reference";

/**
 * @internal For the unified copilot chat surface only
 *
 * The currently registered ambient contextual entities in insertion order.
 */
export const useAmbientContextualEntities = (): ContextualEntity[] => {
  const store = useCopilotStore();
  return useStore(
    store,
    useShallow((state) =>
      [...state.ambientContextualEntities.values()].map(
        (entry) => entry.entity,
      ),
    ),
  );
};

/**
 * @internal For context chips and the invoke payload only.
 *
 * Reads only the ambient source: global entities are never automatic.
 * A positive registrant count means at least one ambient provider opted in,
 * so "true wins" when providers disagree.
 */
export const useAutomaticallyAddedContextualEntities =
  (): ContextualEntity[] => {
    const store = useCopilotStore();
    return useStore(
      store,
      useShallow((state) =>
        [...state.ambientContextualEntities.values()]
          .filter((entry) => entry.agentContextRegistrantCount > 0)
          .map((entry) => entry.entity),
      ),
    );
  };

/**
 * @internal The merged mention catalogue across ambient and global sources.
 *
 * Resources are deduplicated by their parent-aware contextual entity key.
 * For a duplicate, the ambient entity value wins because it is the freshest
 * on-screen representation. Automatic inclusion is resolved separately from
 * the ambient registry, so a matching global entry cannot override an
 * ambient opt-in.
 */
export const useContextualEntities = (): ReferenceContextualEntity[] => {
  const ambientEntities = useAmbientContextualEntities();
  const { entities: globalEntities } = useGlobalContextualEntitiesContext();

  return useMemo(() => {
    const globalEntitiesByKey = globalEntities.reduce(
      (entitiesByKey, entity) => {
        const key = contextualEntityKey(entity);
        if (!entitiesByKey.has(key)) {
          entitiesByKey.set(key, entity);
        }
        return entitiesByKey;
      },
      new Map<string, ReferenceContextualEntity>(),
    );
    const entitiesByKey = ambientEntities
      .filter(isReferenceContextualEntity)
      .reduce((mergedEntitiesByKey, entity) => {
        mergedEntitiesByKey.set(contextualEntityKey(entity), entity);
        return mergedEntitiesByKey;
      }, globalEntitiesByKey);
    return [...entitiesByKey.values()];
  }, [ambientEntities, globalEntities]);
};

/**
 * @internal For the unified copilot chat surface only.
 *
 * Returns the trigger that fans out to every registered refetch callback.
 */
export const useCopilotNotifyMCPMutation = (): (() => void) => {
  const store = useCopilotStore();
  return useStore(store, (state) => state.actions.notifyMCPMutation);
};

/**
 * @internal For the unified copilot chat surface only.
 *
 * Empty-state suggestions declared by the mounted surfaces, in registration
 * order and deduplicated by prompt text. Empty while no surface declares any.
 */
export const useRegisteredSuggestionPrompts = (): ChatEmptyStatePrompt[] => {
  const store = useCopilotStore();
  return useStore(
    store,
    useShallow((state) => {
      const prompts = [...state.suggestionPrompts.values()].flat();
      return prompts.filter(
        (prompt, idx) =>
          prompts.findIndex((other) => other.prompt === prompt.prompt) === idx,
      );
    }),
  );
};
